import { InfoCircleOutlined } from '@ant-design/icons'
import { BusinessProfile } from '@components/business/businessProfile/BusinessProfile'
import { Company } from '@components/business/company/Company'
import Page from '@components/Page/Page'
import Reservation from '@components/reservation/Reservation'
import { TableFilters } from '@components/TableFilters'
import { Venue } from '@components/venue/Venue'
import { TableUrlParams, useTableUrlSync } from '@hooks/useTableUrlSync'
import { Reservation as ReservationModel } from '@models/Reservation'
import {
  getReservations,
  selectReservations,
  setReservationQuery,
} from '@store/business/ReservationReducer'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import { EllipsisMiddle } from '@supplement-bacon/react-toolkit/components'
import {
  initialQuery,
  SBTable,
  TableParams,
} from '@supplement-bacon/sb-table-filters'
import { currencyFormat } from '@utils/currencyFormatter'
import { Entity } from '@utils/Entities'
import { Permission } from '@utils/permissions'
import {
  Col,
  DatePicker,
  Input,
  Space,
  Tag,
  Tooltip,
  Typography,
  type TableColumnsType,
} from 'antd'
import dayjs from 'dayjs'

const { Text } = Typography
const { RangePicker } = DatePicker

const ReservationsPage = () => {
  const dispatch = useAppDispatch()
  const reservations = useAppSelector(selectReservations())
  const { query, isLoading } = useAppSelector((state) => state.reservation)

  const columns: TableColumnsType<ReservationModel> = [
    {
      title: 'Company',
      render: (record: ReservationModel) => (
        <Company.Avatar id={record.company} />
      ),
      fixed: 'left',
    },
    {
      title: 'Employee',
      render: (record: ReservationModel) => (
        <BusinessProfile.Avatar id={record.businessProfile} />
      ),
      fixed: 'left',
    },
    {
      title: 'Guests',
      render: (record: ReservationModel) => <Text>{record.guests}</Text>,
    },
    {
      title: 'Ticket Id',
      render: (record: ReservationModel) => (
        <>
          {record.financial?.ticketId ? (
            <Tooltip title={record.financial?.ticketId}>
              <EllipsisMiddle
                style={{ maxWidth: 250 }}
                suffixCount={6}
                copyable={{ text: record.financial?.ticketId?.toString() }}
              >
                {record.financial?.ticketId?.toString()}
              </EllipsisMiddle>
            </Tooltip>
          ) : (
            '--'
          )}
        </>
      ),
    },
    {
      title: 'Reference',
      render: (record: ReservationModel) => (
        <Text copyable={{ text: record.reference }}>
          <Tooltip title={record.reference}>{record.reference}</Tooltip>
        </Text>
      ),
    },
    {
      title: 'Venue',
      render: (record: ReservationModel) => <Venue.Avatar id={record.venue} />,
    },
    {
      title: 'Date',
      key: 'date',
      render: (record: ReservationModel) => (
        <Text>{dayjs(record.date).format('YYYY-MM-DD HH:mm')}</Text>
      ),
    },
    {
      title: 'POS Amount',
      render: (record: ReservationModel) => (
        <Space orientation="vertical">
          {!record.financial?.posAmount &&
            !record.financial?.inputtedAmount &&
            '--'}
          {record.financial?.posAmount && (
            <Space>
              <Text>{currencyFormat(record.financial?.posAmount, true)}</Text>
              <Tooltip
                title={
                  <Space orientation="vertical">
                    <Space className="w-full space-between ">
                      <Tag>TVA 10%</Tag>
                      {currencyFormat(record.financial?.posVat10Amount, true)}
                    </Space>
                    <Space className="w-full space-between ">
                      <Tag>TVA 20%</Tag>
                      {currencyFormat(record.financial?.posVat20Amount, true)}
                    </Space>
                  </Space>
                }
              >
                <InfoCircleOutlined />
              </Tooltip>
            </Space>
          )}

          {record.financial?.inputtedAmount && (
            <Space>
              <Text>
                {currencyFormat(record.financial?.inputtedAmount, true)}
              </Text>
              <Text style={{ fontSize: 12 }}>(✍️)</Text>
            </Space>
          )}
        </Space>
      ),
    },
    {
      title: 'PMS Amount',
      render: (record: ReservationModel) => (
        <Space orientation="vertical">
          {record.financial?.amount && (
            <Text>{currencyFormat(record.financial?.amount, true)}</Text>
          )}
        </Space>
      ),
    },
  ]

  const fetchReservations = (params: TableUrlParams) => {
    const newQuery: TableParams = {
      ...query,
      with: ['company', 'businessProfile'],
      columnKey: 'date',
      order: 'descend',
      pagination: {
        ...query.pagination,
        current: params.page,
        pageSize: params.pageSize,
      },
      // No isBusiness here any more: GET /business/reservations pins the scope
      // server-side, so the client cannot be the one asserting it.
      filters: params.filters,
      search: params.search,
    }
    dispatch(setReservationQuery(newQuery))
    dispatch(getReservations(newQuery))
  }

  const { handleFiltersChange, handleTableChange, params } =
    useTableUrlSync<ReservationModel>({
      onParamsChange: fetchReservations,
    })

  return (
    <Page
      permissions={Permission.manageBusiness}
      pageTitle={{
        title: 'Reservations',
        level: 4,
        badge: { count: query.pagination?.total, overflowCount: 999999 },
      }}
    >
      <Col span={24}>
        <TableFilters
          initialValues={{
            venues: params.filters.venues?.map(Number),
            companies: params.filters.companies?.map(Number),
            name: params.search.name,
            dateRange:
              params.filters.startDate && params.filters.endDate
                ? [
                    dayjs(params.filters.startDate),
                    dayjs(params.filters.endDate),
                  ]
                : null,
          }}
          onValuesChange={(filters: any, search: any) => {
            const { dateRange, ...otherfilters } = filters

            const dateFilters =
              dateRange && dateRange[0] && dateRange[1]
                ? {
                    startDate: [dayjs(dateRange[0]).format('YYYY-MM-DD')],
                    endDate: [dayjs(dateRange[1]).format('YYYY-MM-DD')],
                  }
                : {}

            const newFilters = {
              ...otherfilters,
              ...dateFilters,
            }

            handleFiltersChange(newFilters, search)
          }}
          left={[
            {
              name: 'venues',
              type: 'filter',
              label: 'Venues',
              input: (
                <SBAsyncSelect
                  placeholder="Search by Venue"
                  allowClear
                  searchFieldName="name"
                  entity={Entity.venue}
                  initialQuery={{
                    ...initialQuery,
                    pagination: {
                      ...initialQuery.pagination,
                      pageSize: 10,
                      current: 1,
                    },
                    filters: {
                      venuesBusiness: [true],
                    },
                  }}
                  selectProps={{ maxTagCount: 'responsive' }}
                />
              ),
            },
            {
              name: 'companies',
              type: 'filter',
              label: 'Companies',
              input: (
                <SBAsyncSelect
                  placeholder="Search by company"
                  allowClear
                  searchFieldName="name"
                  entity={Entity.company}
                  selectProps={{ maxTagCount: 'responsive' }}
                />
              ),
            },
            {
              name: 'name',
              type: 'search',
              label: 'Business profile',
              input: <Input placeholder="Search by Employee" allowClear />,
            },
          ]}
          right={[
            {
              name: 'dateRange',
              type: 'filter',
              label: 'Date range',
              input: <RangePicker className="w-full" placement="bottomRight" />,
            },
            {
              type: 'button',
              input: <Reservation.ExportReservations query={query} />,
            },
          ]}
        />
        <SBTable
          columns={columns}
          dataSource={reservations}
          loading={isLoading}
          onChange={handleTableChange}
          pagination={query.pagination}
          scroll={{ x: 'max-content' }}
        />
      </Col>
    </Page>
  )
}

export default ReservationsPage
